
import React, { useState, useEffect } from 'react';
import LogoGSCInline from './LogoGSCInline';
import { APP_CONFIG } from '../services/config';
import { useGuitarMatch } from '../context/GuitarMatchContext';

interface HeroLandingProps {
  onStart: () => void;
  lang?: 'en' | 'fr';
}

export const HeroLanding: React.FC<HeroLandingProps> = ({ onStart }) => {
  const { t, lang, setActiveOverlay, setHelpTab, setShowSupportHub } = useGuitarMatch();
  const [currentTime, setCurrentTime] = useState("");
  const [hasApiKey, setHasApiKey] = useState<boolean>(true);
  
  const isFr = lang === 'fr';

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString(isFr ? 'fr-FR' : 'en-US', { 
        hour12: false, hour: '2-digit', minute: '2-digit' 
      }));
    }, 1000);

    const checkKey = async () => {
        // Axe 2: Removed any type assertion
        const aistudio = (window as Window & typeof globalThis & { aistudio?: any }).aistudio;
        if (aistudio && typeof aistudio.hasSelectedApiKey === 'function') {
            const selected = await aistudio.hasSelectedApiKey();
            setHasApiKey(selected);
        }
    };
    checkKey();
    return () => clearInterval(timer);
  }, [isFr]);

  const handleKeySelection = async () => {
      // Axe 2: Removed any type assertion
      const aistudio = (window as Window & typeof globalThis & { aistudio?: any }).aistudio;
      if (aistudio && typeof aistudio.openSelectKey === 'function') {
          await aistudio.openSelectKey();
          setHasApiKey(true);
      }
  };

  const handleSupportAccess = () => {
      setHelpTab('MANUAL');
      setShowSupportHub(true);
  };

  return (
    <div className="relative h-[100dvh] gsc-premium-bg flex flex-col select-none overflow-hidden font-sans text-white">
      <div className="gsc-aurora" />
      <div className="fixed inset-0 z-10 pointer-events-none">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.005)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.005)_1px,transparent_1px)] bg-[size:120px_120px] [mask-image:radial-gradient(ellipse_at_center,black,transparent:80%)]"></div>
      </div>

      <div className="relative z-40 flex flex-col items-center justify-between h-full px-4 py-4 md:py-6 overflow-visible">
          <div className="flex flex-col items-center gap-1 animate-fade-in shrink-0">
              <LogoGSCInline size={60} className="text-white opacity-30" />
              <span className="text-[6px] font-black uppercase tracking-[0.6em] text-slate-600 italic">Precision Guitar Ecosystem</span>
          </div>

          <div className="flex flex-col items-center w-full max-w-6xl text-center flex-1 justify-center py-2 overflow-visible">
              <div className="relative flex flex-col items-center group cursor-default shrink-0 px-4 overflow-visible">
                  <span className="text-6xl sm:text-7xl md:text-[100px] font-black italic uppercase leading-[0.75] tracking-tighter text-white mb-2 animate-guitar-float">
                      Guitar
                  </span>
                  <h1 className="text-4xl sm:text-6xl md:text-[110px] font-black italic uppercase leading-none tracking-tighter relative flex flex-wrap justify-center items-center overflow-visible">
                      <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-slate-400">Match&</span>
                      <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-br from-[#FF5F6D] via-[#FF3131] to-[#C21E2A] pr-14 pb-4">Play</span>
                  </h1>
                  
                  {/* BRAND ELEMENTS RE-INTEGRATED */}
                  <div className="flex gap-4 mt-6 animate-fade-in delay-500">
                      <div className="px-4 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/5 backdrop-blur-md">
                          <span className="text-[8px] font-black uppercase tracking-[0.4em] text-cyan-400">Electric Soul</span>
                      </div>
                      <div className="px-4 py-1 rounded-full border border-smart-gold/30 bg-smart-gold/5 backdrop-blur-md">
                          <span className="text-[8px] font-black uppercase tracking-[0.4em] text-smart-gold">Acoustic Richness</span>
                      </div>
                  </div>

                  <h2 className="text-[10px] md:text-[14px] font-black italic uppercase text-slate-500 tracking-[1em] md:tracking-[2.5em] mt-8 pl-[2.5em] animate-pulse">
                      ( Rythm Edition )
                  </h2>
              </div>

              <div className="mt-12 shrink-0 flex flex-col items-center gap-6">
                  {hasApiKey ? (
                    <button 
                        onClick={onStart}
                        className="group relative flex items-center justify-center p-[1px] rounded-full overflow-hidden gsc-btn-primary shadow-[0_20px_50px_rgba(0,0,0,0.5)] bg-gradient-to-r from-[#FF5F6D] via-white/50 to-smart-gold animate-shimmer-fast"
                    >
                        <div className="relative px-10 py-5 md:px-24 md:py-7 bg-[#050505] group-hover:bg-transparent rounded-full flex items-center justify-center gap-6 transition-all duration-700 overflow-hidden border border-white/5">
                            <span className="text-[10px] md:text-xs font-black italic uppercase tracking-[0.5em] text-white">
                                {t('enter_studio')}
                            </span>
                            <div className="w-8 h-8 md:w-12 md:h-12 rounded-full bg-white/5 flex items-center justify-center border border-white/10 group-hover:bg-white transition-all">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                            </div>
                        </div>
                    </button>
                  ) : (
                    <button onClick={handleKeySelection} className="px-10 py-5 bg-smart-gold text-black rounded-full font-black uppercase text-[10px] shadow-2xl gsc-btn-primary">
                        {t('activate_api_key')}
                    </button>
                  )}
                  <button onClick={handleSupportAccess} className="text-[9px] font-black text-slate-600 uppercase tracking-[0.4em] hover:text-white transition-colors">
                    Access Help & Archives
                  </button>
              </div>
          </div>

          <div className="w-full max-w-7xl px-8 flex flex-row justify-between items-center text-[6px] md:text-[8px] font-black text-white/10 uppercase tracking-[0.5em] md:tracking-[1em] shrink-0 pb-2">
              <span className="cursor-default">NEURAL CORE v{APP_CONFIG.ENGINE_VERSION}-STABLE</span>
              <span className="font-mono text-[9px] md:text-[10px] tabular-nums tracking-widest">{currentTime}</span>
          </div>
      </div>

      <style>{`
        @keyframes guitarFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        .animate-guitar-float { animation: guitarFloat 6s ease-in-out infinite; }
        @keyframes shimmer-fast { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        .animate-shimmer-fast { background-size: 200% 200%; animation: shimmer-fast 8s linear infinite; }
      `}</style>
    </div>
  );
};